import { motion } from "framer-motion";
import { ArrowRight, Tag } from "lucide-react";
import { PRODUCTS } from "@/data/content";

const container = {
    hidden: {},
    show: { transition: { staggerChildren: 0.14 } },
};

const card = {
    hidden: { opacity: 0, y: 40 },
    show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } },
};

const ProductsSection = () => {
    return (
        <section id="products" data-testid="products-section" className="relative overflow-hidden py-24 sm:py-28">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute left-1/2 top-0 h-[380px] w-[720px] -translate-x-1/2 rounded-full bg-[#7200C8]/14 blur-[150px]" />
            </div>

            <div className="relative mx-auto max-w-7xl px-5 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 26 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.7 }}
                    className="mx-auto max-w-3xl text-center"
                >
                    <div className="flex items-center justify-center gap-4">
                        <span className="gold-hairline h-px w-14" />
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D9A441]">Crafted By BrothaTruth</p>
                        <span className="gold-hairline h-px w-14" />
                    </div>
                    <h2 data-testid="products-heading" className="mt-6 font-display text-3xl font-bold tracking-tight text-[#F8F5EE] sm:text-4xl lg:text-5xl">
                        Products &amp; <span className="text-gold-gradient">Innovations</span>
                    </h2>
                    <p className="mt-5 text-base leading-relaxed text-[#F8F5EE]/60 sm:text-lg">
                        Physical goods and rare knowledge — created with the same standard of excellence.
                    </p>
                </motion.div>

                <motion.div
                    variants={container}
                    initial="hidden"
                    whileInView="show"
                    viewport={{ once: true, margin: "-60px" }}
                    className="mx-auto mt-16 grid max-w-7xl gap-8 md:grid-cols-2 xl:grid-cols-3"
                >
                    {PRODUCTS.map((product) => (
                        <motion.article
                            key={product.key}
                            variants={card}
                            data-testid={`product-card-${product.key}`}
                            className="group relative flex flex-col overflow-hidden rounded-2xl border border-[#D9A441]/25 bg-white/[0.03] backdrop-blur-sm transition-all duration-500 hover:-translate-y-2 hover:border-[#D9A441]/50 hover:bg-white/[0.05] hover:shadow-[0_24px_60px_-18px_rgba(217,164,65,0.4)]"
                        >
                            <div className="relative h-72 overflow-hidden">
                                <img
                                    src={product.image}
                                    alt={product.alt}
                                    loading="lazy"
                                    className={`h-full w-full transition-transform duration-700 ease-out group-hover:scale-110 ${
                                        product.contain ? "object-contain p-5" : "object-cover"
                                    }`}
                                />
                                <div className="absolute inset-0 bg-gradient-to-t from-[#080B12] via-[#080B12]/20 to-transparent" />
                                <span
                                    data-testid={`product-badge-${product.key}`}
                                    className="absolute left-4 top-4 rounded-full border border-[#D9A441]/60 bg-[#080B12]/85 px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-[#F4C46B] backdrop-blur-md"
                                >
                                    {product.badge}
                                </span>
                                <span
                                    data-testid={`product-price-${product.key}`}
                                    className="absolute right-4 top-4 flex items-center gap-1.5 rounded-full bg-gold-gradient px-4 py-1.5 text-xs font-bold text-[#080B12] shadow-[0_6px_20px_rgba(217,164,65,0.45)]"
                                >
                                    <Tag className="h-3 w-3" />
                                    {product.wasPrice && <span className="font-medium line-through opacity-60">{product.wasPrice}</span>}
                                    {product.price}
                                </span>
                            </div>

                            <div className="flex flex-1 flex-col p-7">
                                <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-[#D9A441]">{product.tagline}</p>
                                <h3 className="mt-2 font-display text-2xl font-bold tracking-wide text-[#F8F5EE]">{product.name}</h3>
                                <p className="mt-3 flex-1 text-sm leading-relaxed text-[#F8F5EE]/60">{product.description}</p>
                                <a
                                    data-testid={`product-cta-${product.key}`}
                                    href={product.url}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="btn-shine mt-6 inline-flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-7 py-3.5 text-xs font-bold uppercase tracking-[0.14em] text-[#080B12] shadow-[0_8px_26px_-10px_rgba(217,164,65,0.7)] transition-transform duration-300 hover:scale-[1.03]"
                                >
                                    {product.cta}
                                    <ArrowRight className="h-4 w-4" />
                                </a>
                            </div>

                            <span
                                className="pointer-events-none absolute inset-x-0 bottom-0 h-[3px] origin-left scale-x-0 bg-gradient-to-r from-[#D9A441] to-transparent transition-transform duration-500 group-hover:scale-x-100"
                                aria-hidden="true"
                            />
                        </motion.article>
                    ))}
                </motion.div>
            </div>
        </section>
    );
};

export default ProductsSection;
